import React, { useCallback } from "react";
import * as Yup from "yup";
import {
  useWSModalCustom,
  WSAlert,
  WSAlertProps,
  WSCard,
  WSDataItem,
  WSForm,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSPillProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { BulkActionReminder } from "../components/BulkActionReminder";
import { BulkActionType } from "../types";

export type WSAlertConfig = React.PropsWithChildren<WSAlertProps>;

type ConfirmationLineItem = {
  label: string;
  value: string;
};

export type ConfirmationCommentConfig = {
  /** Label for the textarea. Defaults to "Comment (optional)" or "Comment *" when required. */
  label?: string;
  /** Helper text shown below the label. */
  description?: string;
  /** Placeholder text for the textarea. */
  placeholder?: string;
  /** When true the field is required and Yup validation is applied. */
  required?: boolean;
  /** Optional pill badge shown next to the label (e.g. "Public" indicator). */
  pill?: WSPillProps;
  /** Optional alert rendered after the comment field (e.g. visibility reminder). Replaces the default BulkActionReminder when provided. */
  alert?: WSAlertConfig;
};

type FormData = { comment: string };

type Props = {
  actionName: string;
  actionType: BulkActionType;
  itemCount: number;
  description?: string;
  lineItems?: ConfirmationLineItem[];
  confirmLabel?: string;
  confirmDestructive?: boolean;
  /** When provided, shows a comment textarea in the confirmation step. */
  confirmComment?: ConfirmationCommentConfig;
  /** When provided, renders a WSAlert between the description and the comment field. */
  confirmAlert?: WSAlertConfig;
} & WSModalCustomProps<string | false>;

const ModalBulkActionConfirmation: React.FC<Props> = props => {
  const handleCancel = useCallback(() => {
    props.onClose(false);
  }, [props]);

  const handleConfirm = useCallback(
    (formData: FormData) => {
      props.onClose(formData.comment);
    },
    [props]
  );

  const validationSchema = props.confirmComment?.required
    ? Yup.object().shape({
        comment: Yup.string().required("Comment is required")
      })
    : undefined;

  const commentLabel =
    props.confirmComment?.label ??
    (props.confirmComment?.required ? "Comment *" : "Comment (optional)");

  return (
    <WSForm<FormData>
      onSubmit={handleConfirm}
      defaultValues={{ comment: "" }}
      validationSchema={validationSchema}
    >
      <WSModal
        title={props.actionName}
        onClose={() => handleCancel()}
        divider
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "No, cancel",
              onClick: handleCancel,
              type: "button"
            },
            {
              kind: "Primary",
              label:
                props.confirmLabel ?? `Yes, ${props.actionName.toLowerCase()}`,
              destructive: props.confirmDestructive
            }
          ]
        }}
      >
        <WSList gap="M">
          <WSText.ParagraphSm color="gray500">
            {props.description ??
              `Are you sure you want to ${props.actionName.toLowerCase()} ${props.itemCount.toLocaleString()} items?`}
          </WSText.ParagraphSm>

          {props.lineItems && props.lineItems.length > 0 && (
            <WSCard>
              {props.lineItems.map(item => (
                <WSDataItem
                  key={item.label}
                  inline
                  label={item.label}
                  value={item.value}
                />
              ))}
            </WSCard>
          )}

          {props.confirmAlert && <WSAlert {...props.confirmAlert} />}

          {props.confirmComment && (
            <WSForm.Field
              name="comment"
              component={WSInputTextarea}
              componentProps={{
                label: commentLabel,
                helperText: props.confirmComment.description,
                placeholder:
                  props.confirmComment.placeholder ??
                  "Provide specific details...",
                pill: props.confirmComment.pill,
                rows: 3
              }}
            />
          )}

          {props.confirmComment?.alert ? (
            <WSAlert {...props.confirmComment.alert} />
          ) : (
            <BulkActionReminder
              actionName={props.actionName}
              actionType={props.actionType}
            />
          )}
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalBulkActionConfirmation = () =>
  useWSModalCustom(ModalBulkActionConfirmation);
